import { useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { QuotesListPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { DealUpdateModal } from 'modules/finance/components/Deals/UpdateModal'
import { QuotesListTable } from 'modules/finance/components/Quotes/QuotesListTable'
import { SalesRootPageOutletContext } from 'modules/finance/pages/SalesRootPage'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const QuotesListPage = () => {
  const {
    company: { id: companyId },
    access,
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { dealId } = useParams()
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const { routesVisibility: salesRootVisibility } = useOutletContext<SalesRootPageOutletContext>()
  const dealIdNumber = dealId ? Number(dealId) : -1
  const isDealsRoute = dealId
  const { data, error, loading } = useQuery(QuotesListPageDocument, {
    variables: {
      dealId: dealIdNumber,
      skipDeal: dealIdNumber == -1,
    },
  })
  const authorized = salesRootVisibility.finance._policies.sidebarSeeFinanceQuotesSubLevelItem.can
  const deal = data?.finance?.deal
  const dealName = deal?.name
  const canCreateQuote = data?.finance?._policies?.quotesCreate?.can ?? false

  useEffect(() => {
    if (Number(dealId)) {
      track(trackingEvents.accounting.deals.viewDealQuotes)
    } else {
      track(trackingEvents.accounting.quotes.viewQuotesPage)
    }
  }, [dealId])

  const handleClickAdd = () => {
    if (canCreateQuote) {
      return
    }
    navigate(navigation.finance.sales.quotes.new.fullPath(), {
      state: { dealId: Number(dealId) },
    })
  }

  const handleOpenModal = (element: string) => {
    setIsUpdateModalOpen(true)
    track(trackingEvents.accounting.deals.startEdit, {
      source: 'sales_deal_details_page',
      element: element,
    })
  }
  const handleDealUpdated = () => {
    track(trackingEvents.accounting.deals.submitEdit, {
      source: 'sales_deal_details_page',
    })
  }

  return (
    <ResponsivePage.Body
      authorized={authorized}
      error={error}
      loading={loading}
      variant="full"
      onActionClick={(key) => {
        switch (key) {
          case 'edit-deal':
            handleOpenModal('header_edit-deal')
            break
          default:
            break
        }
      }}
    >
      <QuotesListTable
        companyId={companyId}
        locale={access.locale}
        dealId={dealId ? Number(dealId) : undefined}
        onClickAdd={handleClickAdd}
        showCustomerColumn={!isDealsRoute}
        showDealColumn={!isDealsRoute}
        canCreateQuote={canCreateQuote}
        dealName={dealName}
      />
      {deal && (
        <DealUpdateModal
          isOpen={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          deal={deal}
          onSuccess={handleDealUpdated}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default QuotesListPage
